<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiBadgeList from '@/components/ui/UiBadgeList.vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useToast } from '@/composables/useToast';
import { MONTHS, titleCase } from '@/utils/format';

useSessionGuard();

const TABS = [
  { id: 1, name: 'Faculty' },
  { id: 2, name: 'Advisers' },
];
const store = useStore();
const toast = useToast();
const year = computed(() => store.getters.getFilterSelected);
const tab = shallowRef(1);

// Grade levels and subjects come from the selected year's class schedules;
// advisers also show the section they advise.
const headers = computed(() => [
  { title: 'Name', key: 'name' },
  ...(tab.value === 2 ? [{ title: 'Advisory class', key: 'room_section' }] : []),
  { title: 'Grade levels', key: 'grade_levels', sortable: false },
  { title: 'Subjects', key: 'subjects', sortable: false },
]);

const { items, loading, error, load } = useListResource(async () => {
  const rows = await apiRequest('get', `/user-details/getAllFaculty/${tab.value}/${year.value}`);
  return (rows || []).map((r) => ({ ...r, name: titleCase(r.name) }));
});
watch([tab, year], load, { immediate: true });

const SERVER = process.env.VUE_APP_SERVER;

function rowActions(person) {
  return [
    { title: 'Print SF2', icon: 'mdi-printer-outline', onClick: () => openSf2(person) },
    { title: 'Print SF10', icon: 'mdi-printer-outline', hidden: tab.value !== 2, onClick: () => printSf10(person) },
  ];
}

function printSf10(person) {
  window.open(`${SERVER}/pdf-generator/getSchoolForm10/${year.value}/${Number(person.id)}/${person.grade_level}`, '_blank');
}

// SF2 (daily attendance) needs a class, subject and month.
const sf2Teacher = shallowRef(null);
const sf2Open = shallowRef(false);
const sf2 = ref({ roomID: null, subjectID: null, month: null });
const rooms = shallowRef([]);
const subjects = shallowRef([]);
const sf2Fields = [
  { key: 'roomID', label: 'Class', type: 'autocomplete', required: true, items: rooms, itemTitle: 'room_section', itemValue: 'id' },
  { key: 'subjectID', label: 'Subject', type: 'autocomplete', required: true, items: subjects, itemTitle: 'subject_title', itemValue: 'id' },
  { key: 'month', label: 'Month', type: 'select', required: true, items: MONTHS, itemTitle: 'name', itemValue: 'id' },
];

async function openSf2(person) {
  sf2Teacher.value = person;
  sf2.value = { roomID: null, subjectID: null, month: null };
  rooms.value = [];
  subjects.value = [];
  sf2Open.value = true;
  const [roomRows, subjectRows] = await Promise.all([
    apiRequest('get', `/subjects/getAllFacultySF2/${year.value}/${person.id}`).catch(() => null),
    apiRequest('get', `/subjects/getAllSubjectSF2/${person.id}`).catch(() => null),
  ]);
  if (Array.isArray(roomRows)) {
    rooms.value = roomRows.map((r) => ({ ...r, room_section: (r.room_section || '').toUpperCase() }));
  } else {
    toast.error('This faculty member has no grade level set.');
  }
  subjects.value = Array.isArray(subjectRows) ? subjectRows : [];
}

function printSf2() {
  const { roomID, subjectID, month } = sf2.value;
  window.open(
    `${SERVER}/pdf-generator/getSchoolForm2/${year.value}/${roomID}/${subjectID}/${month}/${sf2Teacher.value.id}`,
    '_blank',
  );
  sf2Open.value = false;
}
</script>

<template>
  <UiTableCard
    v-model:tab="tab"
    title="Faculty"
    description="Print school forms for teachers and advisers."
    :tabs="TABS"
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search faculty"
    :search-fields="['grade_level', 'grade_levels', 'subjects']"
    empty-text="No faculty found."
    row-label="name"
    :row-actions="rowActions"
  >
    <template #[`item.name`]="{ item }">
      <UiPerson :name="item.name" />
    </template>
    <template #[`item.room_section`]="{ item }">
      <span v-if="item.room_section" class="text-no-wrap">
        {{ item.room_section.toUpperCase() }}
        <span class="text-medium-emphasis">· {{ item.grade_level }}</span>
      </span>
      <span v-else class="text-medium-emphasis">—</span>
    </template>
    <template #[`item.grade_levels`]="{ item }">
      <UiBadgeList :items="item.grade_levels || []" variant="outline" />
    </template>
    <template #[`item.subjects`]="{ item }">
      <UiBadgeList :items="item.subjects || []" />
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="sf2Open"
    title="Print SF2"
    :description="sf2Teacher?.name"
    v-model:values="sf2"
    :fields="sf2Fields"
    submit-text="Print"
    @submit="printSf2"
  />
</template>
